import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Send } from "lucide-react";
import { Shell } from "@/components/Shell";
import { Stopwatch } from "@/components/challenge/Stopwatch";
import { SetQuestion } from "@/components/challenge/SetQuestion";
import { SetResults } from "@/components/challenge/SetResults";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDuration, fmtSGT } from "@/lib/api";
import { useCountdown } from "@/hooks/useCountdown";

export default function DailyChallenge() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/challenge/today"); setState(data); setError(""); return data; }
    catch (e) { setError(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const draftKey = state ? `pl_draft_${user.id}_${state.day}` : null;
  useEffect(() => {
    if (!draftKey) return;
    try { setAnswers(JSON.parse(localStorage.getItem(draftKey) || "{}")); } catch { setAnswers({}); }
  }, [draftKey]);

  const pick = (qid, idx) => {
    const next = { ...answers, [qid]: idx };
    setAnswers(next);
    localStorage.setItem(draftKey, JSON.stringify(next));
  };

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/challenge/today/start"); setState(data); }
    catch (e) { toast.error(errMsg(e)); load(); } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { answers: state.questions.map((q) => ({ question_id: q.question_id, selected_index: answers[q.question_id] ?? null })) };
      const { data } = await api.post("/challenge/today/submit", payload);
      localStorage.removeItem(draftKey);
      setSubmitted(data);
    } catch (e) {
      toast.error(errMsg(e), { id: "submit-error" });
      await load();
    } finally { setBusy(false); setConfirming(false); }
  };

  const untilOpen = useCountdown(state?.status === "upcoming" ? state.start_at : null, state?.server_now);

  if (error) return <Shell><p className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-300" data-testid="challenge-error">{error}</p></Shell>;
  if (!state) return <Shell><p className="eyebrow mt-10" data-testid="challenge-loading">Loading today's challenge…</p></Shell>;

  if (submitted) return <Shell width="wide"><div className="md:mx-auto md:max-w-xl"><SetResults day={submitted.day} totalDays={state.total_days} results={submitted.results} todayScore={submitted.today_score} responseMs={submitted.response_ms} /></div></Shell>;
  if (state.completed) return <Shell width="wide"><div className="md:mx-auto md:max-w-xl"><SetResults day={state.day} totalDays={state.total_days} results={state.results} todayScore={state.today_score} responseMs={state.attempt.response_ms} /></div></Shell>;

  if (state.status !== "live" || state.question_count === 0) {
    return (
      <Shell>
        <div className="panel fade-up p-6" data-testid="challenge-unavailable">
          <p className="eyebrow">{state.status === "upcoming" ? "Coming soon" : state.status === "ended" ? "Season over" : `Day ${state.day} of ${state.total_days}`}</p>
          <h2 className="mt-2 font-display text-2xl font-bold">
            {state.status === "upcoming" && "The competition hasn't started yet"}
            {state.status === "ended" && "The season has ended"}
            {state.status === "live" && "Today's challenges aren't published yet"}
          </h2>
          {state.status === "upcoming" && untilOpen != null && <p className="mt-3 font-mono text-2xl text-blue-400" data-testid="season-opens-countdown">{fmtDuration(untilOpen)}</p>}
          {state.status === "upcoming" && <p className="mt-1 text-sm text-slate-400">Day 1 opens {fmtSGT(state.start_at)} SGT</p>}
          <Link to="/leaderboard" className="btn-secondary mt-6 w-full" data-testid="unavailable-leaderboard-link">View leaderboard</Link>
        </div>
      </Shell>
    );
  }

  if (state.attempt.status === "not_started") {
    return (
      <Shell width="wide">
        <div className="fade-up flex flex-1 flex-col md:mx-auto md:w-full md:max-w-xl" data-testid="set-start-card">
          <p className="eyebrow" data-testid="challenge-day-label">Day {state.day} of {state.total_days}</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight">Today's set: {state.question_count} challenges</h1>
          <ul className="mt-6 space-y-3 text-sm text-slate-300">
            <li className="panel p-4">Points: {state.questions.map((q) => q.points).join(" · ")}. Correct = full points, wrong = 0.</li>
            <li className="panel p-4">Take as long as you need. Your timer starts when you press <span className="font-semibold text-white">Start</span> and stops when you press <span className="font-semibold text-white">Submit</span>.</li>
            <li className="panel p-4">One attempt only. Total time across the 7 days breaks ties on the leaderboard. Today's set closes {fmtSGT(state.day_closes_at)} SGT.</li>
          </ul>
          <button onClick={start} disabled={busy} className="btn-primary mt-8 w-full py-4 text-lg" data-testid="set-start-button"><Play size={20} /> {busy ? "Starting…" : "Start"}</button>
        </div>
      </Shell>
    );
  }

  const answered = state.questions.filter((q) => answers[q.question_id] != null).length;
  const allAnswered = answered === state.questions.length;
  return (
    <Shell width="wide">
      <div className="sticky top-0 z-10 -mx-5 mb-5 border-b border-slate-800 bg-[#0B0F17]/95 px-5 py-3 backdrop-blur md:-mx-8 md:px-8" data-testid="set-header">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-slate-300" data-testid="challenge-day-label">Day {state.day} of {state.total_days}</p>
          <Stopwatch startedAt={state.attempt.started_at} serverNow={state.server_now} />
        </div>
        <div className="mt-2 flex gap-1.5" data-testid="set-progress">
          {state.questions.map((q) => <span key={q.question_id} className={`h-1.5 flex-1 rounded-full ${answers[q.question_id] != null ? "bg-blue-500" : "bg-slate-800"}`} />)}
        </div>
      </div>
      <div className="space-y-8 md:grid md:grid-cols-3 md:items-start md:gap-6 md:space-y-0">
        {state.questions.map((q) => <SetQuestion key={q.question_id} question={q} total={state.question_count} selected={answers[q.question_id] ?? null} onSelect={(i) => pick(q.question_id, i)} disabled={busy} />)}
      </div>
      <div className="mt-8 md:mx-auto md:max-w-md" data-testid="set-submit-area">
        {confirming ? (
          <div className="panel p-5">
            <p className="text-sm text-slate-200">Submit all {state.question_count} answers? {answered < state.question_count && <span className="text-amber-300">{state.question_count - answered} unanswered will score 0.</span>} This cannot be undone.</p>
            <div className="mt-4 flex gap-3">
              <button type="button" className="btn-secondary flex-1" onClick={() => setConfirming(false)} disabled={busy} data-testid="set-submit-cancel-button">Keep editing</button>
              <button type="button" className="btn-primary flex-1" onClick={submit} disabled={busy} data-testid="set-submit-confirm-button">{busy ? "Submitting…" : "Yes, submit"}</button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn-primary w-full py-4 text-lg" disabled={answered === 0 || busy} onClick={() => (allAnswered ? submit() : setConfirming(true))} data-testid="set-submit-button"><Send size={18} /> Submit {answered}/{state.question_count}</button>
        )}
        <p className="mt-3 text-center text-xs text-slate-500">You can change answers until you submit. Submitting stops your timer.</p>
      </div>
    </Shell>
  );
}
